/*
 * Padel USA — giveaway entry + countdown.
 * Owner: sections/giveaway_custom.liquid. Dossier: custom-code-padelusa/features/giveaway.md
 *
 * Scalable model (no per-month list creation):
 *  - Subscribe an entrant to ONE permanent "Giveaway Entrants" list (created once; its ID is a
 *    stable section setting) ONLY when they tick a consent box. Email and SMS consent are separate,
 *    optional opt-ins (unticked by default); entering never requires either.
 *  - Fire an "Entered Giveaway" Klaviyo EVENT per entry, carrying the giveaway id/name, prize, and
 *    UTM attribution. The event is the per-entry record; the monthly winner draw = filter this
 *    event by giveaway_id. Each month "resets" automatically — only the metaobject record changes.
 *  - Stamp the profile with current_giveaway + utm_* so segments/attribution are one click in Klaviyo.
 *
 * Everything is client-side with the PUBLIC company_id only. See the section header for why this
 * diverges from the native {% form 'customer' %} newsletter.
 *
 * PRESERVE: the data-* hook names, the honeypot ([name="gv_hp"]), and the metric name
 * "Entered Giveaway" (the winner-draw + reporting filter on the Klaviyo side keys off it).
 */
(function () {
  'use strict';

  // 2025-07-15 is the first revision where the client /subscriptions/ endpoint accepts the
  // `subscriptions` consent object on the profile; older revisions (e.g. 2024-10-15) reject it
  // with "'subscriptions' is not a valid field for the resource 'profile'". Verified 202 across
  // /client/subscriptions/, /client/events/, /client/profiles/.
  var KLAVIYO_REVISION = '2025-07-15';
  var SUBSCRIBE_URL = 'https://a.klaviyo.com/client/subscriptions/';
  var PROFILE_URL = 'https://a.klaviyo.com/client/profiles/';
  var EVENT_URL = 'https://a.klaviyo.com/client/events/';
  var EVENT_METRIC = 'Entered Giveaway';
  var NOTIFY_METRIC = 'Giveaway Notify Signup';

  function pad(n) { return n < 10 ? '0' + n : '' + n; }

  function isEmail(v) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v); }

  /* Best-effort E.164 normalization, US-default. Returns null if implausible. */
  function normalizePhone(raw) {
    var trimmed = (raw || '').trim();
    if (!trimmed) return null;
    if (trimmed.charAt(0) === '+') {
      var rest = trimmed.slice(1).replace(/\D/g, '');
      return (rest.length >= 8 && rest.length <= 15) ? '+' + rest : null;
    }
    var digits = trimmed.replace(/\D/g, '');
    if (digits.length === 10) return '+1' + digits;
    if (digits.length === 11 && digits.charAt(0) === '1') return '+' + digits;
    if (digits.length >= 8 && digits.length <= 15) return '+' + digits;
    return null;
  }

  /* Pull UTM params (and referrer fallback) so we can attribute entries to social/ads. */
  function collectAttribution() {
    var props = {};
    try {
      var q = new URLSearchParams(window.location.search);
      ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(function (k) {
        var v = q.get(k);
        if (v) props[k] = v;
      });
    } catch (e) { /* no-op */ }
    if (!props.utm_source && document.referrer) { props.referrer = document.referrer; }
    return props;
  }

  function initCountdown(el) {
    var endTs = parseInt(el.getAttribute('data-end-ts'), 10);
    if (!endTs) return;
    var cells = {
      days: el.querySelector('[data-cd="days"]'),
      hours: el.querySelector('[data-cd="hours"]'),
      minutes: el.querySelector('[data-cd="minutes"]'),
      seconds: el.querySelector('[data-cd="seconds"]')
    };

    function tick() {
      var now = Math.floor(Date.now() / 1000);
      var diff = endTs - now;
      if (diff <= 0) {
        if (cells.days) cells.days.textContent = '0';
        if (cells.hours) cells.hours.textContent = '00';
        if (cells.minutes) cells.minutes.textContent = '00';
        if (cells.seconds) cells.seconds.textContent = '00';
        clearInterval(timer);
        return;
      }
      var d = Math.floor(diff / 86400);
      var h = Math.floor((diff % 86400) / 3600);
      var m = Math.floor((diff % 3600) / 60);
      var sec = diff % 60;
      if (cells.days) cells.days.textContent = d;
      if (cells.hours) cells.hours.textContent = pad(h);
      if (cells.minutes) cells.minutes.textContent = pad(m);
      if (cells.seconds) cells.seconds.textContent = pad(sec);
    }

    var timer = setInterval(tick, 1000);
    tick();
  }

  function initForm(form) {
    var companyId = form.getAttribute('data-company-id');
    var smsEnabled = form.getAttribute('data-sms') === 'true';
    var giveawayName = form.getAttribute('data-month') || '';
    var giveawayId = form.getAttribute('data-giveaway-id') || giveawayName;
    var prize = form.getAttribute('data-prize') || '';
    var msgEl = form.querySelector('[data-giveaway-msg]');
    var submitBtn = form.querySelector('[type="submit"]');
    // 'entry' = enter a live giveaway (fires Entered Giveaway). 'notify' = no giveaway running;
    // just subscribe to the list so we can email them when the next one is live.
    var mode = form.getAttribute('data-mode') || 'entry';

    function setMsg(text, state) {
      if (!msgEl) return;
      msgEl.textContent = text || '';
      if (state) { msgEl.setAttribute('data-state', state); }
      else { msgEl.removeAttribute('data-state'); }
    }

    function showSuccess(withPhone, withConfirm) {
      while (form.firstChild) { form.removeChild(form.firstChild); }
      var ok = document.createElement('p');
      ok.className = 'giveaway__msg';
      ok.setAttribute('data-state', 'success');
      ok.setAttribute('role', 'status');
      // withConfirm: a marketing consent was sent, so Klaviyo's double opt-in confirmation follows.
      ok.textContent = (mode === 'notify')
        ? 'You’re on the list! 🎾 Check your inbox to confirm — we’ll email you when the next giveaway is live.'
        : (withConfirm
          ? 'You’re in! 🎾 Check your inbox' + (withPhone ? ' and phone' : '') + ' to confirm your subscription.'
          : 'You’re in! 🎾 Good luck — the winner is announced on this page.');
      form.appendChild(ok);
    }

    if (!companyId) {
      setMsg('Entries open soon.', 'error');
      if (submitBtn) submitBtn.disabled = true;
      return;
    }

    /* Best-effort: stamp the profile so Klaviyo segments/attribution are one click. */
    function tagProfile(email, attribution) {
      var properties = { entry_source: (mode === 'notify') ? 'giveaway-notify' : 'giveaway-page' };
      if (mode === 'notify') {
        properties.giveaway_notify = 'true';
      } else {
        properties.current_giveaway = giveawayId;
        properties.last_giveaway_prize = prize;
      }
      for (var k in attribution) { if (attribution.hasOwnProperty(k)) properties[k] = attribution[k]; }
      try {
        fetch(PROFILE_URL + '?company_id=' + encodeURIComponent(companyId), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION },
          body: JSON.stringify({ data: { type: 'profile', attributes: { email: email, properties: properties } } })
        });
      } catch (e) { /* non-blocking */ }
    }

    /* The per-entry record. Winner draw + source reporting filter on this event. */
    function fireEntryEvent(email, phone, attribution) {
      var props = {};
      if (mode !== 'notify') { props.giveaway = giveawayName; props.giveaway_id = giveawayId; props.prize = prize; }
      for (var k in attribution) { if (attribution.hasOwnProperty(k)) props[k] = attribution[k]; }
      var profileAttrs = { email: email };
      if (phone) profileAttrs.phone_number = phone;
      try {
        fetch(EVENT_URL + '?company_id=' + encodeURIComponent(companyId), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION },
          body: JSON.stringify({
            data: {
              type: 'event',
              attributes: {
                metric: { data: { type: 'metric', attributes: { name: (mode === 'notify') ? NOTIFY_METRIC : EVENT_METRIC } } },
                properties: props,
                profile: { data: { type: 'profile', attributes: profileAttrs } }
              }
            }
          })
        });
      } catch (e) { /* non-blocking */ }
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      setMsg('', '');

      // Honeypot: real users never fill this.
      var hp = form.querySelector('[name="gv_hp"]');
      if (hp && hp.value) { return; }

      var listId = form.getAttribute('data-list-id');
      if (!listId) { setMsg('This giveaway isn’t open yet — check back soon.', 'error'); return; }

      var emailField = form.querySelector('[name="email"]');
      var email = emailField ? emailField.value.trim() : '';
      if (!isEmail(email)) { setMsg('Please enter a valid email address.', 'error'); return; }

      // Email consent is a separate, optional opt-in (Sam 2026-10-12 / 2026-10-19): entering never
      // requires it, and an unticked box must never subscribe. Notify mode exists only to email the
      // person, so there the box is required.
      var emailConsentEl = form.querySelector('[name="email_consent"]');
      var emailConsent = !!(emailConsentEl && emailConsentEl.checked);
      if (mode === 'notify' && !emailConsent) {
        setMsg('Tick the box so we can email you when the next giveaway opens.', 'error');
        return;
      }

      var smsConsentEl = form.querySelector('[name="sms_consent"]');
      var smsConsent = !!(smsEnabled && smsConsentEl && smsConsentEl.checked);
      var phoneField = form.querySelector('[name="phone"]');
      var phoneRaw = phoneField ? phoneField.value : '';
      var phoneE164 = phoneRaw.trim() ? normalizePhone(phoneRaw) : null;
      if (phoneRaw.trim() && smsConsent && !phoneE164) {
        setMsg('Please enter a valid mobile number, or uncheck the text option.', 'error');
        return;
      }

      var subscriptions = {};
      if (emailConsent) { subscriptions.email = { marketing: { consent: 'SUBSCRIBED' } }; }
      if (phoneE164 && smsConsent) { subscriptions.sms = { marketing: { consent: 'SUBSCRIBED' } }; }
      var wantsSubscription = !!(subscriptions.email || subscriptions.sms);

      var profileAttrs = { email: email };
      if (phoneE164) { profileAttrs.phone_number = phoneE164; }
      if (wantsSubscription) { profileAttrs.subscriptions = subscriptions; }

      var payload = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Giveaway page',
            profile: { data: { type: 'profile', attributes: profileAttrs } }
          },
          relationships: { list: { data: { type: 'list', id: listId } } }
        }
      };

      var attribution = collectAttribution();
      var prevLabel = submitBtn ? submitBtn.textContent : '';
      if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = (mode === 'notify') ? 'Saving…' : 'Entering…'; }

      // No consent given: skip the list subscription entirely. The entry itself is the
      // "Entered Giveaway" event (fired below), so it still counts for the draw.
      var subscribe = wantsSubscription
        ? fetch(SUBSCRIBE_URL + '?company_id=' + encodeURIComponent(companyId), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION },
          body: JSON.stringify(payload)
        })
        : Promise.resolve({ ok: true, status: 202 });

      subscribe.then(function (res) {
        if (res.status === 202 || res.ok) {
          tagProfile(email, attribution);
          fireEntryEvent(email, phoneE164, attribution);
          showSuccess(smsConsent && !!phoneE164, wantsSubscription);
          return;
        }
        return res.json().then(function (j) {
          var detail = (j && j.errors && j.errors[0] && j.errors[0].detail) ||
            'Something went wrong. Please try again.';
          throw new Error(detail);
        });
      }).catch(function (err) {
        if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = prevLabel; }
        setMsg((err && err.message) || 'Network error. Please try again.', 'error');
      });
    });
  }

  function ready(fn) {
    if (document.readyState !== 'loading') { fn(); }
    else { document.addEventListener('DOMContentLoaded', fn); }
  }

  ready(function () {
    var counters = document.querySelectorAll('[data-giveaway-countdown]');
    for (var i = 0; i < counters.length; i++) { initCountdown(counters[i]); }
    var forms = document.querySelectorAll('[data-giveaway-form]');
    for (var j = 0; j < forms.length; j++) { initForm(forms[j]); }
  });
})();
